import { useState, useCallback } from 'react'
import type { Editor } from '@tiptap/react'

interface UseLinkEditorProps {
  editor: Editor | null
}

/**
 * Hook to manage link editor state and actions
 * Provides functions to add, edit, and remove links in the editor
 */
export const useLinkEditor = ({ editor }: UseLinkEditorProps) => {
  const [isEditingLink, setIsEditingLink] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  const handleSetLink = useCallback(() => {
    if (!editor) return

    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      setIsEditingLink(false)
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run()
    setIsEditingLink(false)
    setLinkUrl('')
  }, [editor, linkUrl])

  const handleCancelLink = useCallback(() => {
    setIsEditingLink(false)
    setLinkUrl('')
  }, [])

  const handleStartEditLink = useCallback(() => {
    if (!editor) return
    const previousUrl = editor.getAttributes('link').href || ''
    setLinkUrl(previousUrl)
    setIsEditingLink(true)
  }, [editor])

  return {
    isEditingLink,
    linkUrl,
    setLinkUrl,
    handleSetLink,
    handleCancelLink,
    handleStartEditLink,
  }
}
